import { useLayoutEffect, useRef, type RefObject, type KeyboardEvent, type SyntheticEvent } from 'react'

let scrollLocks = 0
let previousOverflow = ''

/** Native modal isolation, plus deterministic Tab wrapping and nested focus restoration. */
export function useModalDialog({ open = true, onClose, preventClose = false, initialFocus, returnFocus, fallbackFocus }: {
  open?: boolean
  onClose: () => void
  preventClose?: boolean
  initialFocus?: RefObject<HTMLElement>
  returnFocus?: RefObject<HTMLElement>
  fallbackFocus?: RefObject<HTMLElement>
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const options = useRef({ onClose, preventClose, initialFocus, returnFocus, fallbackFocus })
  options.current = { onClose, preventClose, initialFocus, returnFocus, fallbackFocus }

  useLayoutEffect(() => {
    if (!open || !ref.current) return
    const dialog = ref.current
    const previous = options.current.returnFocus?.current ?? document.activeElement as HTMLElement | null
    if (scrollLocks++ === 0) {
      previousOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    }
    const fitViewport = () => {
      // Modal roots can be children of space-y layouts; their margins must stay zero.
      dialog.style.margin = '0'
      dialog.style.height = `${window.visualViewport?.height ?? window.innerHeight}px`
      dialog.style.top = `${window.visualViewport?.offsetTop ?? 0}px`
    }
    fitViewport()
    window.visualViewport?.addEventListener('resize', fitViewport)
    window.visualViewport?.addEventListener('scroll', fitViewport)
    window.addEventListener('resize', fitViewport)
    if (!dialog.open) dialog.showModal()
    ;(options.current.initialFocus?.current ?? dialog).focus({ preventScroll: true })
    return () => {
      window.visualViewport?.removeEventListener('resize', fitViewport)
      window.visualViewport?.removeEventListener('scroll', fitViewport)
      window.removeEventListener('resize', fitViewport)
      if (dialog.open) dialog.close()
      if (--scrollLocks === 0) document.body.style.overflow = previousOverflow
      queueMicrotask(() => {
        const target = previous?.isConnected && !previous.matches(':disabled') ? previous : options.current.fallbackFocus?.current
        target?.focus({ preventScroll: true })
      })
    }
  }, [open])

  const onCancel = (event: SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (event.target === event.currentTarget && !options.current.preventClose) options.current.onClose()
  }
  const onKeyDown = (event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key !== 'Tab' || (event.target as HTMLElement).closest('dialog[open]') !== ref.current) return
    const elements = Array.from(ref.current?.querySelectorAll<HTMLElement>('a[href],button,input,select,textarea,[tabindex]') ?? [])
      .filter(element => element.tabIndex >= 0 && !element.matches(':disabled') && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden')
    const index = elements.indexOf(document.activeElement as HTMLElement)
    if (!elements.length || index < 0 || (event.shiftKey ? index === 0 : index === elements.length - 1)) {
      event.preventDefault()
      ;(event.shiftKey ? elements[elements.length - 1] : elements[0])?.focus()
    }
  }
  return { ref, onCancel, onKeyDown }
}
